import { InfoIconAnchor } from '@/components/formFields/InfoIconAnchor';
import { If } from '@/components/utils/conditionalComponents';
import { Children, ReturnComponent, Style } from '@/utils/reactTypes';
import { Box, InputLabel, SxProps, Tooltip, Typography, useTheme } from '@mui/material';
import { ComponentProps } from 'react';

export const labelWithRequiredAsterisk = ({
    label,
    required,
    enabled = true,
}: {
    label: string;
    required: boolean;
    enabled?: boolean;
}): string => {
    if (required && enabled) {
        return label + ' *';
    }
    return label;
};

export const AxleLabelTypography = ({ children }: { children: Children }) => {
    const theme = useTheme();
    return (
        <Typography
            sx={{
                fontStyle: 'normal',
                padding: theme.spacing(1, 0),
                // textTransform: 'uppercase',
                fontWeight: '700',
                fontSize: '14px',
                lineHeight: '14px',
                color: 'black',
                '.Mui-disabled': {
                    color: 'secondary',
                },
            }}
        >
            {children}
        </Typography>
    );
};

export const AxleLabel = ({
    label,
    required = false,
    enabled = true,
    disabledHelperText = '',
    bigLabel = false,
    style = {},
    description,
    ...rest
}: {
    label: string;
    required?: boolean;
    bigLabel?: boolean;
    style?: Style;
} & Omit<ComponentProps<typeof InfoIconAnchor>, 'children' | 'icon'>): ReturnComponent => {
    const theme = useTheme();

    // Styling.
    if (bigLabel) {
        style = {
            fontSize: 20,
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 900,
            letterSpacing: '0rem',
            textTransform: 'none',
            marginBottom: 1,
            ...style,
        };
    }

    return (
        <InfoIconAnchor
            enabled={enabled}
            disabledHelperText={disabledHelperText}
            description={description}
            {...rest}
        >
            <InputLabel
                disabled={!enabled}
                sx={{
                    fontStyle: 'normal',
                    padding: theme.spacing(1, 0),
                    // textTransform: 'uppercase',
                    fontWeight: '700',
                    fontSize: '14px',
                    lineHeight: '14px',
                    color: 'black',
                    ...style,
                    '.Mui-disabled': {
                        color: 'secondary',
                    },
                }}
            >
                {labelWithRequiredAsterisk({ label, required, enabled })}
            </InputLabel>
        </InfoIconAnchor>
    );
};

export const FieldWithAxleLabel = ({
    label,
    tooltipText,
    inlineLabel = false,
    required = false,
    enabled = true,
    disabledHelperText = '',
    description,
    children,
    sx = {},
    ...rest
}: {
    label: string | undefined;
    tooltipText?: string;
    inlineLabel?: boolean;
    required?: boolean;
    enabled?: boolean;
    disabledHelperText?: string;
    description?: Children;
    children: Children;
    sx?: SxProps;
}): ReturnComponent => (
    <>
        <Box sx={sx} {...rest}>
            <If condition={!inlineLabel}>
                <If condition={!!label}>
                    <Tooltip title={tooltipText} placement='top-end'>
                        {/* The Box component is required for the tooltip component to show up on the Axle Label. Do not remove*/}
                        <Box>
                            <AxleLabel
                                label={label ?? ''}
                                required={required}
                                description={description}
                                enabled={enabled}
                                disabledHelperText={disabledHelperText}
                            />
                        </Box>
                    </Tooltip>
                </If>
            </If>
            {children}
        </Box>
    </>
);
